import type { AppUser } from '@/domain/user'
import type { Episode } from '@/domain/episode'
import type { QuizResult } from '@/domain/result'
import type { EpisodePerformanceEntry } from '@/domain/analytics'
import type { SarahContext } from '@/domain/sarah'

const KNOWN_TOPICS = ['phishing', 'sim-swap', 'passwords']

function canonicalTopic(episode?: Episode | null): string | undefined {
  return episode?.topicTags.find((t) => KNOWN_TOPICS.includes(t))
}

export interface BuildSarahContextParams {
  user: AppUser
  episode?: Episode | null
  quizResult?: QuizResult | null
  // Org-wide weak-area signal, already computed by AnalyticsRepository
  // (P2) — buildSarahContext never recalculates this itself, it only
  // repackages the one entry the calling page already fetched.
  weakestOrgEpisode?: EpisodePerformanceEntry | null
  completedEpisodeCount?: number
  totalEpisodeCount?: number
  cyberPoints?: number
  badgesEarned?: number
}

// Pure transform: assembles the minimal SarahContext handed to the AI layer
// from data the calling page has already fetched via the normal service
// abstractions. This function makes no repository calls and duplicates no
// P1 scoring or P2 analytics math — it only repackages numbers/records that
// already exist (P3 architecture requirement: "do not duplicate analytics
// logic inside Sarah").
export function buildSarahContext(params: BuildSarahContextParams): SarahContext {
  const { user, episode, quizResult, weakestOrgEpisode, completedEpisodeCount, totalEpisodeCount, cyberPoints, badgesEarned } =
    params

  const episodeTopic = canonicalTopic(episode)
  const missedTopics: string[] = []
  if (quizResult && !quizResult.passed && episodeTopic) {
    missedTopics.push(episodeTopic)
  }

  return {
    learnerFirstName: user.displayName.split(' (')[0].split(' ')[0],
    role: user.role,
    episodeTitle: episode?.title,
    episodeTopics: episodeTopic ? [episodeTopic] : undefined,
    learningObjectives: episode?.learningObjectives,
    quizScorePercent: quizResult?.scorePercent,
    quizPassed: quizResult?.passed,
    missedTopics: missedTopics.length > 0 ? missedTopics : undefined,
    weakestOrgTopic: weakestOrgEpisode
      ? { episodeTitle: weakestOrgEpisode.title, passRatePercent: weakestOrgEpisode.passRatePercent ?? 0 }
      : undefined,
    completedEpisodeCount,
    totalEpisodeCount,
    cyberPoints,
    badgesEarned,
  }
}
